import { transitionsFor } from '@/shared/config/event.config';
import { useEventConfig } from '@/shared/config/event-config-context';
import type { Participant } from '@/shared/types/pax';
import { IconButton } from '@/shared/ui/IconButton';
import { CheckIcon, CloseIcon } from '@/shared/ui/icons';

interface RowActionsProps {
  participant: Participant;
  currentGroup: string;
  pending: boolean;
  onMove: (participant: Participant, target: string) => void;
}

interface ActionMeta {
  label: (name: string) => string;
  icon: typeof CheckIcon;
  tone: 'approve' | 'reject';
}

/**
 * Verbo e ícone de cada destino. Não vem da parametrização: o documento traz o
 * nome do status ("Aprovados"), não a ação que leva até ele ("Aprovar").
 *
 * Status que a API traga e não esteja aqui recebe o rótulo genérico — a linha
 * continua operável em vez de perder o botão.
 */
const ACTION_META: Record<string, ActionMeta> = {
  aprovados: { label: name => `Aprovar ${name}`, icon: CheckIcon, tone: 'approve' },
  naoaprovados: { label: name => `Não aprovar ${name}`, icon: CloseIcon, tone: 'reject' },
  emaprovacao: { label: name => `Devolver ${name} para análise`, icon: CheckIcon, tone: 'approve' },
};

function actionMetaFor(key: string, label: string): ActionMeta {
  return ACTION_META[key] ?? { label: name => `Mover ${name} para ${label}`, icon: CheckIcon, tone: 'approve' };
}

/**
 * Ações disponíveis para uma linha.
 * Quais transições existem vem de `transitionsFor`, não de condicionais aqui.
 */
export function RowActions({ participant, currentGroup, pending, onMove }: RowActionsProps) {
  const { groups } = useEventConfig();
  const labels = new Map(groups.map(group => [group.key, group.label]));

  return (
    <div className="flex items-center justify-end gap-1.5">
      {transitionsFor(
        currentGroup,
        groups.map(group => group.key),
      ).map(target => {
        const label = labels.get(target) ?? target;
        const meta = actionMetaFor(target, label);
        const Icon = meta.icon;

        return (
          <IconButton
            key={target}
            label={meta.label(participant.nome)}
            tooltip={label}
            tone={meta.tone}
            loading={pending}
            bordered={false}
            onClick={() => onMove(participant, target)}
            icon={<Icon />}
          />
        );
      })}
    </div>
  );
}
